import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useId, type ReactNode } from "react";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { STATES } from "../states";
import { CopyButton } from "./CopyButton";
import { Field, isPending, PendingBadge, safeHref } from "./Field";
import { StatusBadge, VerificationBadge, type PublicVerification } from "./StatusBadge";

type Item = Record<string, string | null>;
type Block = { general: Item[] | { pending: true }; state: Item[] | { pending: true } | null };
export type ProgramDetailData = {
  program: Record<string, unknown> & { name: string; localName: string | null; status: string;
    // A fact is a value, or {pending:true} while unverified. stateNotes is null when no state variant has notes.
    status_note?: string | { pending: true } | null; stateNotes?: string | { pending: true } | null; verification: PublicVerification };
  origin: Record<string, "federal" | "state">;
  category: { slug: string; name: string; parent: { slug: string; name: string } | null };
  department: { slug: string; name: string; acronym: string | null } | null;
  stateDepartment: { slug: string; name: string; acronym: string | null } | null;
  lists: Record<"contacts" | "documents" | "steps" | "faqs" | "denial_reasons", Block>;
  stateVerification: PublicVerification | null;
  sources: { url: string; title: string | null; publisher: string | null }[];
};

const LINKS: [string, string][] = [["apply_url", "Apply"], ["official_url", "Official page"],
  ["eligibility_checker_url", "Eligibility checker"], ["office_locator_url", "Office locator"]];

/** A link with a copy button; a pending value shows the badge, an empty one nothing. */
export function UrlRow({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined || value === "") return null;
  const href = safeHref(value);
  return (
    <div className="flex items-center justify-between gap-2 py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      {isPending(value) ? <PendingBadge /> : (
        <span className="flex min-w-0 items-center gap-1">
          {href ? (
            <a href={href} target="_blank" rel="noreferrer noopener" className="flex items-center gap-1 truncate text-brand-700 hover:underline dark:text-brand-100">
              {new URL(href).hostname}<ExternalLink size={12} />
            </a>
          ) : <span className="truncate">{String(value)}</span>}
          <CopyButton text={String(value)} />
        </span>
      )}
    </div>
  );
}

function ListBlock({ title, block, render, stateCode }: { title: string; block: Block; render: (i: Item) => ReactNode; stateCode?: string }) {
  const part = (items: Block["general"] | null, heading?: string) => {
    if (items === null) return null;
    if (isPending(items)) return <div>{heading && <div className="label">{heading}</div>}<PendingBadge /></div>;
    if (!items.length) return null;
    return <div>{heading && <div className="label">{heading}</div>}<ul className="flex flex-col gap-1.5">{items.map((i) => <li key={i.id}>{render(i)}</li>)}</ul></div>;
  };
  const s = part(block.state, stateCode ? `${stateCode} specific` : undefined);
  const g = part(block.general, block.state ? "General" : undefined);
  if (!s && !g) return null;
  return <section className="card flex flex-col gap-3"><h2 className="text-sm font-semibold">{title}</h2>{s}{g}</section>;
}

/** One program merged for the caller's state: the full page, or the narrower side panel (links and contacts first). */
export function ProgramView({ slug, state, onStateChange, layout = "page" }: {
  slug: string; state?: string; onStateChange: (state: string | undefined) => void; layout?: "page" | "panel";
}) {
  const { me } = useAuth();
  const stateSelectId = useId();
  const q = useQuery({
    queryKey: ["program", slug, state],
    queryFn: () => api<ProgramDetailData>(`/api/directory/programs/${encodeURIComponent(slug)}${state ? `?state=${state}` : ""}`),
  });
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (q.error) return <p role="alert" className="text-sm text-red-600">{q.error instanceof ApiError && q.error.status === 404 ? "Program not found." : "Could not load this program."}</p>;
  const d = q.data!;
  const p = d.program;

  const header = (
    <div>
      <div className="text-xs text-slate-500">
        <Link to="/directory" search={{ category: d.category.parent?.slug ?? d.category.slug }} className="hover:underline">{d.category.parent?.name ?? d.category.name}</Link>
        {d.category.parent && <> / {d.category.name}</>}
      </div>
      <div className="flex items-center gap-3"><h1 className="text-xl font-semibold">{p.name}</h1>
        {me?.user.role === "admin" && <Link to="/admin/programs/$id" params={{ id: String(p.id) }} className="btn-ghost text-xs">Edit</Link>}</div>
      {p.localName && <div className="text-sm text-brand-700 dark:text-brand-100">In {state}: {p.localName}</div>}
      <div className="mt-1 flex flex-wrap items-center gap-3"><StatusBadge status={p.status} /><VerificationBadge v={p.verification} />
        {d.stateVerification && <span className="text-xs text-slate-500">{state}: <VerificationBadge v={d.stateVerification} /></span>}</div>
      {p.status === "discontinued" && (
        <p className="mt-2 rounded-md bg-slate-100 px-3 py-2 text-sm">
          {isPending(p.status_note) ? <>Status note: <PendingBadge /></> : String(p.status_note ?? "This program is no longer taking applications.")}
        </p>
      )}
    </div>
  );

  const facts = (
    <>
      {state && p.stateNotes !== null && p.stateNotes !== undefined && (
        <section className="card"><Field label={`Notes for ${state}`} value={p.stateNotes} /></section>
      )}
      <section className="card grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><Field label="Summary" value={p.summary} origin={d.origin.summary} /></div>
        <div className="sm:col-span-2"><Field label="Description" value={p.description} /></div>
        <Field label="Who it is for" value={p.who_for} />
        <Field label="What the customer can get" value={p.benefits} />
        <div className="sm:col-span-2"><Field label="Eligibility" value={p.eligibility} origin={d.origin.eligibility} /></div>
        <Field label="Income rules" value={p.income_rules} origin={d.origin.income_rules} />
        <Field label="Limitations" value={p.limitations} />
        <Field label="Processing time" value={p.processing_time} origin={d.origin.processing_time} />
        <Field label="If the application is denied" value={p.denial_next_steps} />
      </section>
      <ListBlock title="Documents to have ready" block={d.lists.documents} stateCode={state} render={(i) => <span className="text-sm">{i.item}{i.note && <span className="text-slate-500"> · {i.note}</span>}</span>} />
      <ListBlock title="How to apply" block={d.lists.steps} stateCode={state} render={(i) => <span className="text-sm">{i.text}</span>} />
      <ListBlock title="Common questions" block={d.lists.faqs} stateCode={state} render={(i) => (
        <details className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800"><summary className="cursor-pointer text-sm font-medium">{i.question}</summary><p className="mt-1 whitespace-pre-line text-sm">{i.answer}</p></details>
      )} />
      <ListBlock title="Common reasons for denial" block={d.lists.denial_reasons} stateCode={state} render={(i) => (
        <span className="text-sm"><b>{i.reason}</b>{i.what_to_do && <> · {i.what_to_do}</>}</span>
      )} />
      {d.sources.length > 0 && (
        <section className="card"><h2 className="mb-2 text-sm font-semibold">Sources</h2>
          <ul className="text-xs">{d.sources.map((s) => <li key={s.url}>{safeHref(s.url) ? <a href={s.url} target="_blank" rel="noreferrer noopener" className="text-brand-700 hover:underline dark:text-brand-100">{s.title ?? s.url}</a> : <span>{s.title ?? s.url}</span>}{s.publisher && ` · ${s.publisher}`}</li>)}</ul>
        </section>
      )}
    </>
  );

  const side = (
    <aside className="flex flex-col gap-4">
      <section className="card">
        {/* "State shown", not "Caller's state": the top bar's control has that name and is on screen beside this one. */}
        <label className="label" htmlFor={stateSelectId}>State shown</label>
        <select id={stateSelectId} className="input" value={state ?? ""} onChange={(e) => onStateChange(e.target.value || undefined)}>
          <option value="">General (all states)</option>
          {STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </section>
      <section className="card">
        <h2 className="mb-1 text-sm font-semibold">Official links</h2>
        {LINKS.map(([k, label]) => <UrlRow key={k} label={label} value={p[k]} />)}
        {LINKS.every(([k]) => p[k] === null || p[k] === undefined || p[k] === "") && <p className="text-sm text-slate-400">Not listed</p>}
      </section>
      <ListBlock title="Contacts" block={d.lists.contacts} stateCode={state} render={(c) => (
        <div className="text-sm"><div className="text-xs text-slate-500">{c.label}{c.hours && ` · ${c.hours}`}</div>
          <div className="flex items-center justify-between gap-2"><span className="font-medium">{c.value}</span><CopyButton text={c.value ?? ""} /></div></div>
      )} />
      {(d.department || d.stateDepartment) && (
        <section className="card text-sm"><h2 className="mb-1 text-sm font-semibold">Run by</h2>
          {[d.stateDepartment, d.department].filter(Boolean).map((x) => (
            <Link key={x!.slug} to="/directory/departments/$slug" params={{ slug: x!.slug }} className="block text-brand-700 hover:underline dark:text-brand-100">{x!.name}{x!.acronym && ` (${x!.acronym})`}</Link>
          ))}
        </section>
      )}
    </aside>
  );

  if (layout === "panel") return <div className="flex flex-col gap-4">{header}{side}{facts}</div>;
  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3">
      <div className="flex flex-col gap-4 lg:col-span-2">{header}{facts}</div>
      {side}
    </div>
  );
}
